import React, { useEffect, useState } from "react";
import api from "@/lib/api";

// Module-level cache: an already-downloaded image is reused across pages
// instead of being re-fetched on every navigation.
const blobCache = new Map();
const inflight = new Map();

function loadImage(path, guest) {
  const key = (guest ? "g:" : "") + path;
  if (blobCache.has(key)) return Promise.resolve(blobCache.get(key));
  if (inflight.has(key)) return inflight.get(key);
  const url = guest ? `/public/image/${path}` : `/files/${path}`;
  const p = api
    .get(url, { responseType: "blob" })
    .then((res) => {
      const objectUrl = URL.createObjectURL(res.data);
      blobCache.set(key, objectUrl);
      inflight.delete(key);
      return objectUrl;
    })
    .catch((e) => {
      inflight.delete(key);
      throw e;
    });
  inflight.set(key, p);
  return p;
}

/**
 * Authenticated image loader. Fetches via axios (with cookie), converts to blob URL.
 * Use for images served by /api/files/{path} that require an auth session.
 * When `guest` is set, loads the public (no-auth) image route instead.
 */
export default function AuthImage({ path, alt = "", className = "", fallback = null, guest = false }) {
  const cacheKey = path ? (guest ? "g:" : "") + path : null;
  const [src, setSrc] = useState(() => (cacheKey ? blobCache.get(cacheKey) || null : null));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!path) { setFailed(true); return; }
    const cached = blobCache.get(cacheKey);
    if (cached) { setSrc(cached); setFailed(false); return; }
    let cancelled = false;
    setSrc(null);
    setFailed(false);
    loadImage(path, guest)
      .then((url) => { if (!cancelled) setSrc(url); })
      .catch((e) => {
        console.error("[AuthImage] load failed for", path, e);
        if (!cancelled) setFailed(true);
      });
    return () => { cancelled = true; };
  }, [path, guest, cacheKey]);

  if (failed) return fallback;
  if (!src) return <div className={`${className} bg-zinc-100 animate-pulse`} />;
  return <img src={src} alt={alt} loading="lazy" decoding="async" className={className} />;
}
